import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSEO } from "../lib/seo";
import { Reveal, Overline } from "../components/Reveal";
import { HomeHero } from "../components/HomeHero";
import { Marquee } from "../components/Marquee";
import { EstimateForm } from "../components/EstimateForm";
import { ServiceCards } from "../components/ServiceCards";
import { RealResults } from "../components/RealResults";
import { WhyChoose } from "../components/WhyChoose";
import { FAQAccordion } from "../components/FAQAccordion";
import { GoogleReviews } from "../components/GoogleReviews";
import { CTABand } from "../components/CTABand";
import { GENERAL_FAQS } from "../lib/faqs";
import { IMAGES } from "../lib/constants";

const Home = () => {
  useSEO(
    "Carpet Cleaning Gold Coast | Kay's Carpet Cleaning",
    "Professional carpet, couch, rug, mattress, tile & grout and commercial cleaning on the Gold Coast. 7+ years of experience. Call Kay's Carpet Cleaning on 0450 311 907 for a free estimate."
  );

  return (
    <>
      <HomeHero />
      <Marquee />

      <section id="estimate" data-testid="home-estimate-section" className="py-20 sm:py-24 bg-white border-y border-brand-line/50">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal className="text-center mb-10">
            <Overline>Free & No-Obligation</Overline>
            <h2 className="mt-4 font-display font-medium text-4xl sm:text-5xl leading-[1.08] tracking-tight text-brand-ink">
              Quick Cleaning <span className="font-serif italic text-brand">Estimate</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <EstimateForm compact />
          </Reveal>
        </div>
      </section>

      <ServiceCards />
      <RealResults />
      <GoogleReviews />
      <WhyChoose />

      <section data-testid="home-about-section" className="py-24 sm:py-32 bg-white border-y border-brand-line/50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-5">
            <div className="overflow-hidden rounded-tl-[4rem] rounded-br-[4rem] rounded-tr-2xl rounded-bl-2xl border border-brand-line/60 shadow-[0_30px_80px_rgba(45,90,83,0.14)]">
              <img
                src={IMAGES.cleaner}
                alt="Kay's Carpet Cleaning professional at work on the Gold Coast"
                loading="lazy"
                className="w-full h-[340px] sm:h-[440px] object-cover"
                data-testid="home-about-image"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-7 lg:pl-8">
            <Reveal>
              <Overline>About Kay's Carpet Cleaning</Overline>
              <h2 className="mt-4 font-display font-medium text-4xl sm:text-5xl leading-[1.1] tracking-tight text-brand-ink">
                Cleaning Gold Coast Homes & Businesses for <span className="font-serif italic text-brand">More Than 10 Years</span>
              </h2>
              <p className="mt-6 text-base sm:text-lg text-brand-muted leading-relaxed">
                Kay's Carpet Cleaning is a Gold Coast local business with more than 7 years of
                practical cleaning experience and thousands of completed cleaning jobs for residential
                and commercial customers. When you call, you talk directly to the people who
                do the work — and you get a straight answer about what your job needs.
              </p>
              <p className="mt-4 text-base sm:text-lg text-brand-muted leading-relaxed">
                Have a cleaning problem you're not sure about? Call us and tell us what you need.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/about" data-testid="home-about-link" className="group inline-flex items-center gap-2.5 bg-brand hover:bg-brand-dark text-white font-semibold px-6 py-3.5 rounded-full text-sm transition-[background-color,transform] duration-300 hover:scale-[0.97]">
                  More About Us <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link to="/faqs" data-testid="home-faq-link" className="inline-flex items-center gap-2.5 border border-brand-line bg-white text-brand-ink font-semibold px-6 py-3.5 rounded-full text-sm transition-[border-color] duration-300 hover:border-brand">
                  Read Our FAQs
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="home-faq-section" className="py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <Reveal className="mb-12">
            <Overline>Common Questions</Overline>
            <h2 className="mt-4 font-display font-medium text-4xl sm:text-5xl leading-[1.08] tracking-tight text-brand-ink">
              Good to <span className="font-serif italic text-brand">know</span>
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <FAQAccordion items={GENERAL_FAQS.slice(0, 5)} testIdPrefix="home-faq" closingNote={false} />
          </Reveal>
          <Reveal delay={0.12} className="mt-8">
            <Link to="/faqs" data-testid="home-all-faqs-link" className="group inline-flex items-center gap-2 text-sm font-semibold text-brand">
              See all frequently asked questions
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32">
        <CTABand id="home-final" heading="Ready for a Cleaner Home or Workplace?" text="Tell us what you need cleaned and Kay's Carpet Cleaning will provide an estimate based on your requirements." primaryLabel="Get My Estimate" />
      </div>
    </>
  );
};

export default Home;
